// View → Preact（render.mjs renderView の移植・全ビュー型ディスパッチ）。
// レスポンシブは layoutCss（media query）を <style> で注入。ビュー固有 CSS も同様。
import {
  resolveTokens,
  layoutCss,
  label,
  groupRows,
  groupAggLabel,
  tableSpecs,
  widthOf,
  keyOf,
  enumChip,
  primaryColId,
  calendarDateCol,
  calendarModel,
  mapLocationCol,
} from "./model.mjs";
import { Block } from "./Block";
import { EditControl } from "./EditControl";
import { IconInbox } from "./icons";
import type { RenderCtx } from "./context";
import type { AppDefinition, View as ViewDef, Row, Column } from "../types";

/** 1 行分のブロックグリッド（list/gallery/board/detail/form 共通の単位）。
 *  selectable=true（一覧系）のときは行タップで onSelectRow を発火する。 */
function Card({ ctx, view, row, selectable }: { ctx: RenderCtx; view: ViewDef; row: Row; selectable?: boolean }) {
  const cols = view.layout.grid.columns;
  const clickable = !!(selectable && ctx.onSelectRow && row.__rowId);
  return (
    <div
      class={"grid card" + (clickable ? " is-clickable" : "")}
      role={clickable ? "button" : undefined}
      onClick={clickable ? () => ctx.onSelectRow?.(row) : undefined}
    >
      {view.layout.blocks.map((b) => (
        <Block key={b.id} block={b} ctx={ctx} row={row} depth={0} gridColumns={cols} />
      ))}
    </div>
  );
}

function GroupedCards({ ctx, view, rows, muted, fg }: { ctx: RenderCtx; view: ViewDef; rows: Row[]; muted: string; fg: string }) {
  if (!view.groupByColumnId) return <>{rows.map((r) => <Card key={r.__rowId} ctx={ctx} view={view} row={r} selectable />)}</>;
  const groups = [...groupRows(rows, view.groupByColumnId).entries()] as Array<[string, Row[]]>;
  return (
    <>
      {groups.map(([k, grows]) => (
        <div key={k}>
          <div class="group-h" style={`color:${fg}`}>
            <span>{k}</span>
            <span style={`color:${muted};font-weight:400`}>{groupAggLabel(view.groupAgg, grows)}</span>
          </div>
          {grows.map((r) => <Card key={r.__rowId} ctx={ctx} view={view} row={r} selectable />)}
        </div>
      ))}
    </>
  );
}

function TableView({ ctx, view, rows, muted }: { ctx: RenderCtx; view: ViewDef; rows: Row[]; muted: string }) {
  const table = ctx.table;
  const specs = tableSpecs(view, table) as Array<{ columnId: string; width?: "default" | "narrow" | "wide" }>;
  const colOf = (id: string): Column | undefined => table?.columns.find((c) => c.id === id);
  const quick = !!view.quickEdit;

  const cell = (c: Column | undefined, row: Row) => {
    const val = c ? row[keyOf(c, c.id)] : "";
    if (!quick || !c) {
      const chip = c ? enumChip(c, val, ctx.tokens, ctx.locale) : null;
      if (chip) return <td><span class="ss-chip" style={`background:${chip.bg};color:${chip.fg}`}>{chip.text}</span></td>;
      return <td>{String(val ?? "")}</td>;
    }
    return (
      <td>
        <EditControl col={c} columnId={c.id} value={val} locale={ctx.locale} compact onInput={(key, v) => ctx.onInput?.(row, key, v)} />
      </td>
    );
  };
  // 行タップで詳細へ（QuickEdit の入力要素上のクリックは遷移させない）
  const rowClick = (row: Row) => (e: MouseEvent) => {
    const tag = (e.target as HTMLElement)?.tagName ?? "";
    if (/^(INPUT|SELECT|TEXTAREA|BUTTON|OPTION|LABEL)$/.test(tag)) return;
    ctx.onSelectRow?.(row);
  };
  const selectable = !!ctx.onSelectRow;
  const bodyRow = (row: Row) => (
    <tr
      key={row.__rowId}
      data-rowid={row.__rowId ?? ""}
      class={selectable ? "is-clickable" : undefined}
      onClick={selectable ? rowClick(row) : undefined}
    >
      {specs.map((s) => cell(colOf(s.columnId), row))}
    </tr>
  );

  const thead = (
    <tr>
      {specs.map((s) => {
        const c = colOf(s.columnId);
        return <th style={`min-width:${widthOf(s.width)};width:${widthOf(s.width)}`}>{label(c?.name, ctx.locale) || s.columnId}</th>;
      })}
    </tr>
  );

  let tbody;
  if (view.groupByColumnId) {
    tbody = [...groupRows(rows, view.groupByColumnId).entries()].map(([k, grows]) => (
      <>
        <tr class="qe-group">
          <td colSpan={specs.length}>
            {k}　<span style={`color:${muted};font-weight:400`}>{groupAggLabel(view.groupAgg, grows)}</span>
          </td>
        </tr>
        {grows.map(bodyRow)}
      </>
    ));
  } else {
    tbody = rows.map(bodyRow);
  }

  return (
    <>
      <div class="as-tablewrap">
        <table class="as-table">
          <thead>{thead}</thead>
          <tbody>{tbody}</tbody>
        </table>
      </div>
      {quick && <div class="qe-note" style={`color:${muted}`}>✎ QuickEdit: セルを直接編集すると保存されます</div>}
    </>
  );
}

function CalendarView({ ctx, view, rows, muted }: { ctx: RenderCtx; view: ViewDef; rows: Row[]; muted: string }) {
  const table = ctx.table;
  const dateCol = calendarDateCol(view, table);
  const now = ctx.evalCtx?.now ? new Date(ctx.evalCtx.now as string | number | Date) : new Date();
  const { year, month, firstDow, daysIn, byDay } = calendarModel(rows, dateCol, now);
  const pcol = primaryColId(view, table);
  const primaryOf = (r: Row) => String((pcol ? r[pcol] : undefined) ?? r.__rowId ?? "");
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(<div class="cal-cell cal-empty" />);
  for (let d = 1; d <= daysIn; d++) {
    const items: Row[] = byDay.get(d) ?? [];
    cells.push(
      <div class="cal-cell">
        <div class="cal-d">{d}</div>
        {items.slice(0, 2).map((r) => <div class="cal-chip">{primaryOf(r)}</div>)}
        {items.length > 2 && <div class="cal-more">+{items.length - 2}</div>}
      </div>
    );
  }
  const dow = ["日", "月", "火", "水", "木", "金", "土"];
  return (
    <>
      <div class="cal-title">
        {year}年{month + 1}月 <span style={`color:${muted};font-size:11px`}>（{dateCol ?? "日付列なし"}）</span>
      </div>
      <div class="cal-grid">
        {dow.map((w) => <div class="cal-dow">{w}</div>)}
        {cells}
      </div>
    </>
  );
}

function MapView({ ctx, view, rows, muted }: { ctx: RenderCtx; view: ViewDef; rows: Row[]; muted: string }) {
  const table = ctx.table;
  const locCol = mapLocationCol(view, table);
  const pcol = primaryColId(view, table);
  const primaryOf = (r: Row) => String((pcol ? r[pcol] : undefined) ?? r.__rowId ?? "");
  return (
    <>
      <div class="as-mapnote">🗺 地図ビュー（実タイルは地図プロバイダ連携が必要・現在は位置一覧を表示）</div>
      <div class="as-maplist">
        {rows.map((row) => (
          <div
            class={"as-pin" + (ctx.onSelectRow ? " is-clickable" : "")}
            onClick={ctx.onSelectRow ? () => ctx.onSelectRow?.(row) : undefined}
          >
            <span class="as-pin-i">📍</span>
            <div>
              <div class="as-pin-t">{primaryOf(row)}</div>
              <div class="as-pin-s" style={`color:${muted}`}>{String((locCol ? row[locCol] : undefined) ?? (locCol ? "" : "位置列なし"))}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/** ビュー固有 CSS（__V__ を view.id に差し替え）。render.mjs の extraCss と一致。 */
function extraCss(view: ViewDef, tokens: Record<string, string>): string {
  const muted = tokens["color.fg.muted"] ?? "#667085";
  const fg = tokens["color.fg.default"] ?? "#111";
  const V = view.id;
  switch (view.type) {
    case "gallery":
      return `[data-view="${V}"] .as-gallery{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;}[data-view="${V}"] .as-gallery .grid.card{margin:0;}`;
    case "board":
      return (
        `[data-view="${V}"] .as-board{display:flex;gap:10px;overflow-x:auto;padding-bottom:6px;}` +
        `[data-view="${V}"] .as-lane{min-width:210px;flex:0 0 auto;}` +
        `[data-view="${V}"] .as-lane-h{font-size:13px;font-weight:600;color:${fg};margin:0 2px 6px;}` +
        `[data-view="${V}"] .as-lane .grid.card{margin-bottom:8px;}`
      );
    case "table": {
      const card = tokens["color.bg.card"] ?? "#fff";
      return (
        `[data-view="${V}"] .as-tablewrap{overflow-x:auto;background:${card};border-radius:8px;box-shadow:0 1px 2px rgba(0,0,0,.08);}` +
        `[data-view="${V}"] .as-table{border-collapse:collapse;width:100%;font-size:13px;}` +
        `[data-view="${V}"] .as-table th{position:sticky;top:0;background:${card};text-align:left;font-size:11px;color:${muted};font-weight:600;padding:8px 10px;border-bottom:2px solid ${muted}33;white-space:nowrap;}` +
        `[data-view="${V}"] .as-table td{padding:6px 10px;border-bottom:1px solid ${muted}22;color:${fg};vertical-align:middle;}` +
        `[data-view="${V}"] .as-table .qe-group td{background:${muted}11;font-weight:600;font-size:12px;}` +
        `[data-view="${V}"] .qe-note{font-size:11px;margin-top:6px;}` +
        `[data-view="${V}"] .as-table .qe-input{width:100%;box-sizing:border-box;min-height:30px;padding:3px 6px;font:inherit;font-size:13px;border:1px solid transparent;border-radius:6px;background:transparent;color:${fg};}` +
        `[data-view="${V}"] .as-table .qe-input:hover{border-color:${muted}55;background:${card};}` +
        `[data-view="${V}"] .as-table .qe-input:focus{outline:none;border-color:${fg};background:${card};}`
      );
    }
    case "calendar": {
      const primary = tokens["color.brand.primary"] ?? "#0b2545";
      const chipBg = tokens["color.bg.accent"] ?? primary;
      return (
        `[data-view="${V}"] .cal-title{font-size:14px;font-weight:600;margin:2px 0 8px;}` +
        `[data-view="${V}"] .cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;}` +
        `[data-view="${V}"] .cal-dow{text-align:center;font-size:11px;color:${muted};padding:2px 0;}` +
        `[data-view="${V}"] .cal-cell{min-height:52px;border:1px solid ${muted}22;border-radius:6px;padding:2px 3px;overflow:hidden;}` +
        `[data-view="${V}"] .cal-empty{border:0;}` +
        `[data-view="${V}"] .cal-d{font-size:11px;color:${muted};}` +
        `[data-view="${V}"] .cal-chip{font-size:10px;background:${chipBg};color:#fff;border-radius:4px;padding:1px 4px;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}` +
        `[data-view="${V}"] .cal-more{font-size:10px;color:${muted};margin-top:2px;}`
      );
    }
    case "map": {
      const chipBg = tokens["color.bg.card"] ?? "#fff";
      return (
        `[data-view="${V}"] .as-mapnote{font-size:11px;color:${muted};background:${chipBg};border:1px dashed ${muted};border-radius:8px;padding:8px;margin-bottom:8px;}` +
        `[data-view="${V}"] .as-pin{display:flex;gap:8px;align-items:center;background:${chipBg};border-radius:8px;padding:8px 10px;margin-bottom:6px;box-shadow:0 1px 2px rgba(0,0,0,.06);}` +
        `[data-view="${V}"] .as-pin-t{font-weight:600;}`
      );
    }
    default:
      return "";
  }
}

export function ViewRenderer({
  app,
  view,
  rows,
  locale,
  mode,
  evalCtx,
  onAction,
  onInput,
  onSelectRow,
  selectedRow,
}: {
  app: AppDefinition;
  view: ViewDef;
  rows: Row[];
  locale?: import("../types").Locale;
  /** 実効カラースキーム（③）。トークン解決を light/dark で切替。 */
  mode?: "light" | "dark";
  evalCtx?: Record<string, unknown>;
  onAction?: (actionId: string, row?: Row) => void;
  onInput?: (row: Row, key: string, value: unknown) => void;
  /** 一覧系ビューでの行タップ（詳細/フォームへの遷移）。 */
  onSelectRow?: (row: Row) => void;
  /** detail/form が対象にする行（未指定なら先頭行）。 */
  selectedRow?: Row | null;
}) {
  const tokens = resolveTokens(app, mode ?? "light");
  const loc = locale ?? app.settings?.defaultLocale ?? "ja";
  const table = app.tables?.find((t) => t.id === view.tableId);
  const editMode = view.type === "form" || view.quickEdit === true;
  const ctx: RenderCtx = {
    app,
    table,
    tokens,
    locale: loc,
    mode: editMode ? "edit" : "read",
    allRows: rows,
    evalCtx,
    onAction,
    onInput,
    onSelectRow,
  };
  const muted = tokens["color.fg.muted"] ?? "#667085";
  const fg = tokens["color.fg.default"] ?? "#111";
  const css = layoutCss(view.id, view.layout, tokens) + extraCss(view, tokens);

  // 一覧系ビューで行が 0 件のときは空状態を出す（真っ白を避ける）。
  const COLLECTION = ["list", "table", "gallery", "board", "map", "calendar"];
  if (COLLECTION.includes(view.type) && rows.length === 0) {
    return (
      <section data-view={view.id}>
        <style>{css}</style>
        <div class="ss-empty">
          <div class="ss-empty-icon"><IconInbox size={30} /></div>
          <div class="ss-empty-t">まだデータがありません</div>
        </div>
      </section>
    );
  }

  let body;
  switch (view.type) {
    case "list":
      body = <GroupedCards ctx={ctx} view={view} rows={rows} muted={muted} fg={fg} />;
      break;
    case "table":
      body = <TableView ctx={ctx} view={view} rows={rows} muted={muted} />;
      break;
    case "gallery":
      body = <div class="as-gallery">{rows.map((r) => <Card key={r.__rowId} ctx={ctx} view={view} row={r} selectable />)}</div>;
      break;
    case "board": {
      const col = view.groupByColumnId;
      const groups = [...groupRows(rows, col).entries()] as Array<[string, Row[]]>;
      body = (
        <div class="as-board">
          {groups.map(([k, grows]) => (
            <div class="as-lane" key={k}>
              <div class="as-lane-h">
                {k} <span style={`color:${muted};font-weight:400`}>{grows.length}</span>
              </div>
              {grows.map((r) => <Card key={r.__rowId} ctx={ctx} view={view} row={r} selectable />)}
            </div>
          ))}
        </div>
      );
      break;
    }
    case "calendar":
      body = <CalendarView ctx={ctx} view={view} rows={rows} muted={muted} />;
      break;
    case "map":
      body = <MapView ctx={ctx} view={view} rows={rows} muted={muted} />;
      break;
    case "dashboard":
      body = <Card ctx={ctx} view={view} row={rows[0] ?? {}} />;
      break;
    case "detail":
    case "form":
    default:
      // 行選択（openRow / createRow）で遷移してきた行を対象にする。未選択なら先頭行。
      body = <Card ctx={ctx} view={view} row={selectedRow ?? rows[0] ?? {}} />;
      break;
  }

  return (
    <section data-view={view.id}>
      <style>{css}</style>
      {body}
    </section>
  );
}
